import React, { useCallback, useMemo } from "react";
import InputField from "../../shared-components/InputField";
import AddButton from "../../shared-components/AddButton";
import { Degrees } from "../../api/Person";
import { XIcon } from "../../shared-components/icons";
import { uniqueUUID } from "../../utils";
import { classNames } from "../../utils";
import { toDateInputValue, isValidDateRange } from "./helpers";
import { useRowForm } from "./useRowForm";
import { PersonEditSectionModal } from "./PersonEditSectionModal";

export interface DegreeFormRow {
  name: string;
  educator: string;
  country: string;
  start_date: string;
  end_date: string;
  key: string;
  external_id?: string | null;
}

function toFormRow(deg: Degrees, key: string): DegreeFormRow {
  return {
    name: deg.name ?? "",
    educator: deg.educator ?? "",
    country: deg.country ?? "",
    start_date: toDateInputValue(deg.start_date) || "",
    end_date: toDateInputValue(deg.end_date) || "",
    key,
    external_id: deg.external_id ?? undefined,
  };
}

function toDegreePayload(row: DegreeFormRow) {
  return {
    name: row.name.trim(),
    educator: row.educator.trim(),
    country: row.country.trim(),
    start_date: row.start_date.trim() || "",
    end_date: row.end_date.trim() || "",
    external_id: row.external_id ?? null,
  };
}

interface EditEducationModalProps {
  open: boolean;
  onClose: () => void;
  initialDegrees: Degrees[];
  onSave: (degrees: Array<ReturnType<typeof toDegreePayload>>) => Promise<void>;
  onDirtyChange: (dirty: boolean) => void;
  isSaving?: boolean;
}

const emptyDegreeRow = (): DegreeFormRow => ({
  name: "",
  educator: "",
  country: "",
  start_date: "",
  end_date: "",
  key: uniqueUUID(),
});

const EditEducationModal: React.FC<EditEducationModalProps> = ({
  open,
  onClose,
  initialDegrees,
  onSave,
  onDirtyChange,
  isSaving = false,
}) => {
  const initialFormRows = useMemo(
    () =>
      initialDegrees.length > 0
        ? initialDegrees.map((d) => toFormRow(d, d.external_id ?? uniqueUUID()))
        : [],
    [initialDegrees]
  );
  const getEmptyRow = useCallback(emptyDegreeRow, []);

  const { rows, updateRow, addRow, removeRow } = useRowForm(
    open,
    initialFormRows,
    getEmptyRow,
    onDirtyChange
  );

  const validRows = rows.every((r) =>
    isValidDateRange(r.start_date, r.end_date)
  );
  const hasValidDegree = rows.some(
    (r) =>
      (r.name.trim() || r.educator.trim()) &&
      isValidDateRange(r.start_date, r.end_date)
  );
  const submitDisabled =
    !validRows || (!hasValidDegree && rows.some((r) => r.name || r.educator));

  const handleSave = useCallback(async () => {
    const payload = rows
      .map(toDegreePayload)
      .filter((d) => d.name || d.educator)
      .filter((d) => isValidDateRange(d.start_date, d.end_date));
    await onSave(payload.length > 0 ? payload : []);
  }, [rows, onSave]);

  return (
    <PersonEditSectionModal
      open={open}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isSaving}
      submitDisabled={submitDisabled}
      saveErrorMessage="Failed to save education. Please try again."
    >
      <div className="pt-2">
        <div className="gap-2 p-4 border border-gray-200 rounded-lg bg-gray-100 space-y-4">
          <h3 className="text-sm font-bold text-gray-800">Education</h3>
          <div className="space-y-4">
            {rows.map((row, idx) => (
              <div key={row.key} className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <InputField
                    label="Degree"
                    value={row.name}
                    handleChange={(e) => updateRow(idx, "name", e.target.value)}
                    name={`deg-name-${idx}`}
                    placeholder=""
                    classes="min-w-0 w-full"
                  />
                  <InputField
                    label="Name of Institution"
                    value={row.educator}
                    handleChange={(e) =>
                      updateRow(idx, "educator", e.target.value)
                    }
                    name={`deg-educator-${idx}`}
                    placeholder=""
                    classes="min-w-0 w-full"
                  />
                  <InputField
                    label="Start Date"
                    type="date"
                    value={row.start_date}
                    handleChange={(e) =>
                      updateRow(idx, "start_date", e.target.value)
                    }
                    name={`deg-start-${idx}`}
                    classes="min-w-0 w-full"
                  />
                </div>
                <div
                  className={classNames(
                    "grid gap-3",
                    rows.length > 1 ? "grid-cols-[1fr_1fr_auto]" : "grid-cols-2"
                  )}
                >
                  <InputField
                    label="End Date"
                    type="date"
                    value={row.end_date}
                    handleChange={(e) =>
                      updateRow(idx, "end_date", e.target.value)
                    }
                    name={`deg-end-${idx}`}
                    classes="min-w-0 w-full"
                  />
                  <InputField
                    label="Country"
                    value={row.country}
                    handleChange={(e) =>
                      updateRow(idx, "country", e.target.value)
                    }
                    name={`deg-country-${idx}`}
                    placeholder=""
                    classes="min-w-0 w-full"
                  />
                  {rows.length > 1 && (
                    <div className="flex items-end pb-1">
                      <button
                        type="button"
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-300"
                        onClick={() => removeRow(idx)}
                        aria-label="Remove education"
                      >
                        <XIcon />
                      </button>
                    </div>
                  )}
                </div>
                {!isValidDateRange(row.start_date, row.end_date) &&
                  (row.start_date || row.end_date) && (
                    <p className="text-sm text-red-600">
                      Start date must be on or before end date.
                    </p>
                  )}
              </div>
            ))}
          </div>
          <AddButton
            text="Add Education"
            onClick={addRow}
            className="text-blue-600 hover:opacity-80 font-medium"
          />
        </div>
      </div>
    </PersonEditSectionModal>
  );
};

export default EditEducationModal;
